const CODE_FENCE_REGEX = /```(\w*)\n([\s\S]*?)```/g;

const HTML_LANGUAGES = new Set(["html", "htm"]);

/**
 * Matches the first block-level HTML tag anywhere in the text.
 * Used to split plain text + raw HTML when the AI outputs without code fences.
 */
const RAW_HTML_BLOCK_RE =
  /<(?:!doctype|html|head|body|div|p|ul|ol|h[1-6]|blockquote|pre|hr|img|table|section|article|main|header|footer|aside|nav|form|style)\b/i;

const isHtmlCode = (language, content) => {
  if (HTML_LANGUAGES.has(language.toLowerCase())) {
    return true;
  }
  if (!language && /^\s*<(!DOCTYPE|html|head|body)/i.test(content)) {
    return true;
  }
  return false;
};

/**
 * Splits a plain-text chunk that may contain raw HTML into
 * markdown + html-preview segments.
 */
const splitRawHtml = (text) => {
  const match = text.match(RAW_HTML_BLOCK_RE);

  if (!match) {
    return [{ type: "markdown", content: text }];
  }

  const before = text.slice(0, match.index).trim();
  const html = text.slice(match.index).trim();
  const segments = [];

  if (before) {
    segments.push({ type: "markdown", content: before });
  }
  if (html) {
    segments.push({ type: "html-preview", content: html });
  }

  return segments;
};

/**
 * Splits AI output text into ordered segments for rendering.
 *
 * - Markdown text → { type: 'markdown', content }
 * - HTML code fences → { type: 'html-preview', content } (raw HTML for iframe)
 * - Raw HTML blocks → { type: 'html-preview', content } (detected by tag)
 * - Non-HTML code fences → re-wrapped as markdown (rendered as <pre><code>)
 *
 * During streaming, incomplete fences won't match and the whole text
 * renders as markdown (marked handles partial fences gracefully).
 *
 * @param {string} text - Raw output text from AI
 * @returns {Array<{type: string, content: string}>}
 */
const parseOutputSegments = (text) => {
  if (!text) {
    return [];
  }

  const segments = [];
  let lastIndex = 0;

  for (const match of text.matchAll(CODE_FENCE_REGEX)) {
    if (match.index > lastIndex) {
      const textContent = text.slice(lastIndex, match.index).trim();
      if (textContent) {
        segments.push(...splitRawHtml(textContent));
      }
    }

    const language = match[1] || "";
    const code = match[2].trim();

    if (isHtmlCode(language, code)) {
      segments.push({ type: "html-preview", content: code });
    } else if (!language && RAW_HTML_BLOCK_RE.test(code)) {
      // Unlabeled code fence containing HTML blocks (e.g. Subject: ...\n<div>...)
      // Split into markdown + html-preview just like raw text.
      segments.push(...splitRawHtml(code));
    } else {
      segments.push({
        type: "markdown",
        content: `\`\`\`${language}\n${code}\n\`\`\``,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    const textContent = text.slice(lastIndex).trim();
    if (textContent) {
      segments.push(...splitRawHtml(textContent));
    }
  }

  if (segments.length === 0 && text.trim()) {
    segments.push({ type: "markdown", content: text });
  }

  return segments;
};

export default parseOutputSegments;
